// traceShellOrigins.js: where a file came from when no tool call wrote it.
//
// extractFileOps classifies by INPUT SHAPE, so a file produced by a script the
// agent ran through Bash has no write op anywhere in the trace: the only tool
// call that ever names it is the read that comes later. A rail row then reads
// "6 reads" for a file whose entire creation sits one turn earlier, inside a
// command, and the reader is left to believe the file arrived from nowhere.
//
// Two tiers, deliberately unequal, because the evidence is:
//
//   WRITE  the statement names the path as a write DESTINATION: a redirect, a
//          cp/mv destination, `tee`, `touch`, `curl -o`, or the trailing output
//          argument of the media converters. The command says it wrote that
//          exact path, so this becomes a real op with a mark on the lane.
//
//   HINT   the statement only ran IN the file's directory (`cd out && python3
//          -c ...`), which is how most generated media actually appears: the
//          filenames live inside the script, never on the command line. That is
//          NOT evidence the statement wrote this file. An agent reading a
//          source file it never created, one turn after building in that
//          directory, fits the same shape exactly. So a hint mints no op and no
//          mark; it is offered as somewhere to look, labeled as what it is.
//
// Promoting HINT to a mark would make the timeline claim authorship it cannot
// support. If that tier ever needs to be visible on the axis, it needs its own
// mark vocabulary, not a borrowed write.

import { shellCommandOf } from "./traceForgeOps.js";
import { splitStatements, tokenize, resolveShellVars, isVarRef } from "../../xray-factory/src/mine/shell.js";

// Bound the fan-out of one pathological command (`for f in *; do ... > $f; done`
// resolves to nothing, but a long literal list can).
const WRITES_PER_COMMAND_CAP = 8;
const STATEMENT_CLIP = 300;
const HINTS_PER_FILE = 3;
// A build command's chain is a handful of steps; a runaway loop is not a chain.
const DERIVATION_CAP = 10;
// This walk runs synchronously in a memo on every click, and a captured command
// can be large - `resolveShellVars` tolerates 20k characters, which is room for
// hundreds of statements. The per-need bound below is exact and does the real
// work; these two are the backstop that keeps the worst case flat whatever shape
// the command has.
//
// MAX_LINK_CHECKS bounds (statement, need) pairs across the WHOLE walk, not per
// round. What it does not cover is the per-statement `usedIdx` lookup around the
// inner loop, which is at most DERIVATION_CAP x statements set reads.
const MAX_LINK_CHECKS = 20_000;
const MAX_PENDING_NEEDS = 64;

// `> f`, `>> f`, `1> f`. Anchored so an arrow inside prose or a `2>&1` fd dup
// (its `&` is excluded from the target) cannot read as a redirection.
const REDIRECT_RE = /(?:^|\s)\d?>>?\s*['"]?([^\s'"|;&<>]+)/g;

// Verbs whose LAST positional argument is a destination path.
const COPY_VERBS = new Set(["cp", "mv"]);
// Media converters, where "last positional is the output" is the whole calling
// convention. A closed set on purpose: the rule is wrong for most programs, and
// these are the ones that produce the images agents then read back.
const CONVERT_VERBS = new Set(["convert", "magick", "ffmpeg"]);
const FANOUT_VERBS = new Set(["tee", "touch"]);
const FETCH_VERBS = new Set(["curl", "wget"]);

// Statements that consult a directory rather than produce in it. Only used to
// keep the HINT tier from pointing at every `ls` in the trace. A verb missing
// from here costs a noisier hint, never a false write.
const INSPECT_VERBS = new Set([
  "ls", "cat", "head", "tail", "grep", "rg", "find", "wc", "file", "stat", "echo", "printf",
  "pwd", "which", "type", "du", "df", "tree", "diff", "less", "more", "sort", "uniq", "cut",
  "jq", "xxd", "od", "identify", "git", "rm", "mkdir", "true", "test", "export", "source",
]);

const clip = (s, n) => {
  const t = String(s || "").trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const firstLine = (s) => String(s || "").split("\n")[0];

// Everything before the first redirection: `cp a b > log` must not read `log`
// as the copy's destination.
const argSection = (line) => String(line || "").split(/(?:^|\s)\d?>>?/)[0];

function normalizeSlashes(p) {
  return String(p || "").replace(/\\/g, "/");
}

// Collapse `.` and `..` without resolving symlinks: the same lexical answer a
// reader gets by eye, which is all the path comparison here needs.
function lexicalNormalize(p) {
  const segs = normalizeSlashes(p).split("/");
  const out = [];
  for (const s of segs) {
    if (s === "." ) continue;
    if (s === "..") {
      if (out.length > 1) out.pop();
      continue;
    }
    out.push(s);
  }
  return out.join("/") || "/";
}

/** Comparison key for two spellings of one path. Case is preserved; separators are not. */
export function pathKey(p) {
  return lexicalNormalize(p).replace(/\/+$/, "");
}

const isAbsolute = (p) => /^\//.test(p) || /^[A-Za-z]:\//.test(p) || /^~\//.test(p);

function resolveAgainst(target, cwd) {
  const t = normalizeSlashes(target);
  if (!t) return null;
  if (isAbsolute(t)) return lexicalNormalize(t);
  if (!cwd) return null;
  return lexicalNormalize(`${cwd.replace(/\/+$/, "")}/${t}`);
}

const dirOf = (p) => {
  const norm = pathKey(p);
  const at = norm.lastIndexOf("/");
  return at <= 0 ? "" : norm.slice(0, at);
};

const baseOf = (p) => {
  const norm = normalizeSlashes(p).replace(/\/+$/, "");
  return norm.slice(norm.lastIndexOf("/") + 1);
};

// A target we can neither resolve nor stand behind: a variable the grammar
// cannot see through, an unexpanded glob, a shell construct, a device sink.
function unusableTarget(p) {
  if (!p) return true;
  if (isVarRef(p)) return true;
  if (/[$`*?{}[\]()<>|]/.test(p)) return true;
  if (/^\/dev\//.test(p)) return true;
  if (/^-/.test(p)) return true;
  return !baseOf(p);
}

const GLOB_CHARS = /[*?]/;

function globToRe(pattern) {
  const body = String(pattern)
    .replace(/[.+^${}()|[\]\\]/g, "\\$&")
    .replace(/\*/g, "[^/]*")
    .replace(/\?/g, "[^/]");
  return new RegExp(`^${body}$`);
}

// The program a statement runs: leading env assignments and a subshell paren
// are not it, and an absolute program path reads as its basename.
function verbOf(toks) {
  for (const t of toks) {
    const bare = t.replace(/^[({]+/, "");
    if (!bare) continue;
    if (/^[A-Za-z_]\w*=/.test(bare)) continue;
    return baseOf(bare).toLowerCase();
  }
  return "";
}

function positionalsOf(line) {
  const out = [];
  let seenVerb = false;
  for (const raw of tokenize(argSection(line))) {
    const t = raw.replace(/^[({]+/, "");
    if (!t || t === "&" || t === "&&") continue;
    if (/^[A-Za-z_]\w*=/.test(t)) continue;
    if (!seenVerb) {
      seenVerb = true;
      continue;
    }
    if (t.startsWith("-")) continue;
    out.push(t);
  }
  return out;
}

function flagValue(line, names) {
  const toks = tokenize(argSection(line));
  for (let i = 1; i < toks.length; i += 1) {
    const t = toks[i];
    const eq = t.indexOf("=");
    if (eq > 0 && names.has(t.slice(0, eq))) return t.slice(eq + 1);
    if (names.has(t)) return toks[i + 1] || null;
  }
  return null;
}

const OUTPUT_FLAGS = new Set(["-o", "-O", "--output", "--output-document"]);

const looksLikeFile = (p) => /\.[A-Za-z0-9]{1,8}$/.test(baseOf(p));

/**
 * One command's statements in execution order, each carrying the working
 * directory in force when it ran. `cd` is tracked rather than emitted: it
 * produces nothing, and it is what makes every later statement's relative
 * paths and directory hints resolvable.
 */
export function shellStatements(command) {
  const resolved = resolveShellVars(String(command || ""));
  const spans = splitStatements(resolved);
  // Which span the command's exit status actually belongs to. Needed because a
  // chain's exit code is the LAST statement's, and nothing before it can be
  // held to it (see attributableFailure).
  let lastSpan = -1;
  spans.forEach((st, i) => {
    if (firstLine(st.text).trim()) lastSpan = i;
  });
  const out = [];
  let cwd = null;
  spans.forEach((st, i) => {
    const line = firstLine(st.text);
    if (!line.trim()) return;
    const toks = tokenize(argSection(line));
    const verb = verbOf(toks);
    if (verb === "cd" || verb === "pushd") {
      const target = positionalsOf(line)[0] || null;
      // A bare `cd`, `cd -` or an unresolvable relative hop leaves the working
      // directory UNKNOWN. Carrying the previous one forward would attribute
      // later work to a directory the command had already left.
      cwd = target && target !== "-" ? resolveAgainst(target, cwd) : null;
      return;
    }
    out.push({ line, verb, cwd, last: i === lastSpan });
  });
  return out;
}

// Output lines that name a real write failure. Deliberately narrow: matched
// against the target's own basename, so an unrelated "No such file" elsewhere in
// a long build log cannot condemn this write.
const WRITE_FAILURE_RE = /No such file or directory|Permission denied|Read-only file system|No space left|cannot (?:stat|create|open|remove|access|copy)|Is a directory|Not a directory/i;

// The basename has to appear as a WHOLE name, not as a substring. `a.jpg` sits
// inside `data.jpg`, so a bare substring test reads an error about a different
// file as this write's own and drops the hedge the reader needed. A name is
// bounded by a separator, a quote, or the ends of the line - which is every form
// these tools print: `cp: cannot stat 'a.jpg'`, `tee: a.jpg: ...`,
// `cannot create regular file '/out/a.jpg'`.
const NAME_LEFT = "(?:^|[\\s'\"`/=(])";
const NAME_RIGHT = "(?:$|[\\s'\"`:,;)\\]])";
const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function namesWriteFailure(text, path) {
  const base = baseOf(path);
  if (!text || !base) return false;
  const named = new RegExp(`${NAME_LEFT}${escapeRe(base)}${NAME_RIGHT}`);
  for (const line of String(text).split(/\r?\n/)) {
    if (named.test(line) && WRITE_FAILURE_RE.test(line)) return true;
  }
  return false;
}

/**
 * Whether a command's non-zero exit can be held against THIS write.
 *
 * A shell command is a chain and its exit status is the last statement's. An
 * agent's build command routinely ends in a check - `&& grep -c TODO`, `&& test
 * -f out`, `&& diff a b` - and `grep -c` exits 1 when it finds ZERO matches,
 * which is the success the agent was testing for. Measured over 14 days of
 * staging: of 243 write ops from errored shell calls, 205 carried an exit code
 * that was not theirs, across 20 endpoints. It is not one skill's idiom, it is
 * structural - a write almost always sits mid-chain, so the exit almost never
 * belongs to it.
 *
 * This decides what the READING says, NOT what the mark says. The mark stays
 * failed either way, because we do not know where the chain broke and the two
 * available errors are not equal: a write wrongly marked failed costs a reader
 * one click, and the reading they land on corrects it. A write wrongly marked
 * clean costs them a belief, and nothing ever corrects it, because nobody opens
 * a green mark. The self-correcting error is the one to prefer.
 *
 * True means the failure is this write's to own: it is the last statement, so
 * the exit is its own, or the output names a failure of that path. False means
 * the command failed somewhere and this write may well have landed first, which
 * is what the op line then says.
 */
function attributableFailure(st, resultText, path) {
  return Boolean(st.last) || namesWriteFailure(resultText, path);
}

/**
 * The paths a statement says it wrote, resolved against its working directory.
 * `known` maps pathKey → the path as the file ops spell it, and is what lets a
 * `cp src/*.jpg out/` claim land on the files it actually produced.
 */
function writeTargetsOf(st, known) {
  const out = [];
  const push = (p) => {
    if (unusableTarget(p)) return;
    const abs = resolveAgainst(p, st.cwd);
    if (abs) out.push(abs);
    else {
      // A relative target with no known working directory is only usable if the
      // file ops name exactly one file with that basename, otherwise we would
      // be inventing a location.
      const base = baseOf(p).toLowerCase();
      const matches = [...known.values()].filter((k) => baseOf(k).toLowerCase() === base);
      if (matches.length === 1) out.push(matches[0]);
    }
  };

  for (const m of st.line.matchAll(REDIRECT_RE)) push(m[1]);

  const { verb } = st;
  if (FANOUT_VERBS.has(verb)) {
    for (const p of positionalsOf(st.line)) push(p);
  } else if (FETCH_VERBS.has(verb)) {
    const target = flagValue(st.line, OUTPUT_FLAGS);
    if (target) push(target);
  } else if (CONVERT_VERBS.has(verb)) {
    const pos = positionalsOf(st.line);
    const last = pos[pos.length - 1];
    if (last && looksLikeFile(last)) push(last);
  } else if (COPY_VERBS.has(verb)) {
    const pos = positionalsOf(st.line);
    if (pos.length >= 2) {
      const dest = pos[pos.length - 1];
      const sources = pos.slice(0, -1);
      const destDir = resolveAgainst(dest, st.cwd);
      const destIsDir = /\/$/.test(dest) || !looksLikeFile(dest);
      if (!destIsDir) push(dest);
      else if (destDir) {
        // A directory destination names its files only through the sources. A
        // literal source contributes its basename; a glob contributes whichever
        // known files it matches, and nothing when it matches none.
        for (const src of sources) {
          const base = baseOf(src);
          if (!base) continue;
          if (GLOB_CHARS.test(base)) {
            const re = globToRe(base);
            for (const k of known.values()) {
              if (dirOf(k) === pathKey(destDir) && re.test(baseOf(k))) push(k);
            }
          } else if (!unusableTarget(src)) {
            push(`${destDir.replace(/\/+$/, "")}/${base}`);
          }
        }
      }
    }
  }
  return out;
}

// ── Derivation inside one command ───────────────────────────────────────────
//
// A build command is a chain, and the write that lands an artifact is its LAST
// link. `cp s-06.jpg outputs/_qa/` is the statement that names the path, so it is
// the statement the write tier claims - but the pixels came from the `pdftoppm`
// before it, which rendered a PDF that `soffice` produced from a pptx that `node
// build_deck.js` had just rebuilt. All of that is in the captured command text,
// one statement apart, and the rail was showing only the copy.
//
// The chain is recovered by joining statements on the paths they mention: the
// statement that wrote the artifact needs some path, and the latest earlier
// statement that mentions that path is where it came from. The join IS the
// filter, which is what lets path extraction be permissive - a token that is not
// really a path joins to nothing and drops out.

// Programs whose output argument is a PREFIX, not a filename: `pdftoppm in.pdf s`
// writes `s-06.jpg`. Without this the chain breaks at exactly the step that made
// the bytes, because no statement appears to write the file the copy reads.
const PREFIX_WRITERS = new Set(["pdftoppm", "pdftocairo"]);
const PREFIX_OUTPUT_RE = /^-\d+\.(?:jpe?g|png|ppm|pbm|tiff?|svg)$/i;

// Every path-like token a statement mentions, whatever role it plays. A flag, a
// bare word and a variable reference are not paths; an extension or a separator
// is enough to be a candidate.
function statementPaths(st) {
  const out = [];
  for (const t of positionalsOf(st.line)) {
    if (unusableTarget(t)) continue;
    if (!looksLikeFile(t) && !t.includes("/")) continue;
    const abs = resolveAgainst(t, st.cwd);
    if (abs) out.push(abs);
  }
  for (const m of st.line.matchAll(REDIRECT_RE)) {
    if (unusableTarget(m[1])) continue;
    const abs = resolveAgainst(m[1], st.cwd);
    if (abs) out.push(abs);
  }
  return [...new Set(out)];
}

// Does this statement produce `path` under a prefix convention? Matched by
// pattern rather than by expanding the prefix, because the page numbering and its
// zero-padding are the program's business, not something to re-derive.
function prefixProduces(st, path) {
  if (!PREFIX_WRITERS.has(st.verb)) return false;
  const pos = positionalsOf(st.line);
  const prefix = pos[pos.length - 1];
  if (!prefix || looksLikeFile(prefix)) return false;
  const abs = resolveAgainst(prefix, st.cwd);
  if (!abs) return false;
  const key = pathKey(path);
  return key.startsWith(pathKey(abs)) && PREFIX_OUTPUT_RE.test(key.slice(pathKey(abs).length));
}

const stemOf = (p) => {
  const b = baseOf(p);
  const dot = b.lastIndexOf(".");
  return dot > 0 ? b.slice(0, dot) : b;
};

// The weakest link, and the one without which most chains break: a converter
// names its INPUT and derives its output's name from it. `soffice --convert-to
// pdf deck.pptx` writes `deck.pdf` and says so nowhere. Two files that share a
// directory and a stem across a transform step are the same work, and the
// alternative is a chain that stops at exactly the step that did the converting.
// Tagged `stem` so the reading can present it as the inference it is.
// The statement must ALSO name the target's extension. Without that clause both
// `node build_deck.js deck.pptx` and `soffice --convert-to pdf deck.pptx` match a
// needed `deck.pdf` equally, and the deck build then credits the wrong step with
// producing the PDF. `--convert-to pdf` says what it is making; the builder does
// not.
function sameStemIn(candidates, needed, line) {
  const dir = dirOf(needed);
  const stem = stemOf(needed);
  const ext = baseOf(needed).slice(stem.length + 1);
  if (!ext || !new RegExp(`(?:^|[\\s=./])${ext}(?:$|[\\s"'])`, "i").test(line)) return false;
  return candidates.some((p) => p !== needed && dirOf(p) === dir && stemOf(p) === stem);
}

// Directness of the evidence tying a statement to a path, for picking between
// candidates. `wrote` is the statement saying so; `stem` is an inference.
const LINK_RANK = { wrote: 3, prefix: 2, mentions: 1, stem: 0 };

// Verbs that remove a path. They mention it and did not produce it, so letting
// one link a chain would report a deletion as the origin of the file.
const DESTRUCTIVE_VERBS = new Set(["rm", "rmdir", "unlink", "shred"]);

// How a statement was tied to the path the next step needed, strongest first.
// Each rule is a different quality of evidence and the reading says which.
function linkFor(st, writes, paths, needed) {
  if (writes.includes(needed)) return "wrote";
  if (DESTRUCTIVE_VERBS.has(st.verb)) return null;
  if (prefixProduces(st, needed)) return "prefix";
  if (paths.includes(needed)) return "mentions";
  if (sameStemIn(paths, needed, st.line)) return "stem";
  return null;
}

/**
 * What a statement CONSUMED: the path-like tokens that are not its own output.
 *
 * A write's destination directory is not an input, and treating it as one is not
 * a subtle error - on a real build command the `mkdir -p outputs/_qa` right
 * before the copy became the copy's "origin", and the chain stopped there,
 * one statement short of everything that mattered. Directories are excluded
 * outright: the chain traces artifacts, and a directory is not one.
 */
function inputsOf(st, writeKeys) {
  const dirs = new Set(writeKeys.map(dirOf));
  return statementPaths(st)
    .map(pathKey)
    .filter((p) => !writeKeys.includes(p) && !dirs.has(p) && looksLikeFile(p));
}

/**
 * How one path came to exist, within the command that wrote it.
 *
 * Returns the statements oldest-first, each with the path it handed to the next
 * step and how it was tied to it, ending at the statement that wrote `target`.
 * Empty when no statement in this command wrote that path.
 *
 * Deliberately scoped to ONE command. Crossing commands means crossing the
 * container and host mount views of the same directory, which is a separate
 * identity problem; within a command every path is in one namespace.
 */
export function commandDerivation(command, target, known = new Map()) {
  const sts = shellStatements(command);
  const writes = sts.map((st) => writeTargetsOf(st, known).map(pathKey));
  const paths = sts.map((st) => statementPaths(st).map(pathKey));
  const key = pathKey(target);

  let at = -1;
  for (let i = sts.length - 1; i >= 0; i--) {
    if (writes[i].includes(key)) { at = i; break; }
  }
  if (at < 0) return [];

  const inputs = sts.map((st, i) => inputsOf(st, writes[i]));
  // A need carries the index of the statement that wanted it: a producer has to
  // run BEFORE its consumer, and that bound is per-need rather than one moving
  // cursor. A single cursor walking back from the last link makes a sibling's
  // producer unreachable the moment the walk passes it, which silently narrows
  // the chain to whichever input happened to be explained first.
  const chain = [{ idx: at, st: sts[at], produced: target, how: "wrote" }];
  const usedIdx = new Set([at]);
  const produced = new Set([key]);
  let needs = inputs[at].map((path) => ({ path, before: at }));

  let checks = 0;
  let exhausted = false;
  while (!exhausted && needs.length && chain.length < DERIVATION_CAP) {
    // A need can only have been produced BEFORE the statement that wanted it, so
    // the scan never looks past the furthest pending consumer. That bound is
    // exact, not a heuristic, and on a real build command it is a handful of
    // statements rather than the whole command.
    const limit = needs.reduce((m, n) => Math.max(m, n.before), 0);
    // Strongest evidence first, then EARLIEST. Nearest-first picked a reader
    // over the producer: a build command runs `node build_deck.js deck.pptx`
    // and then validates that same pptx, and the validation sits closer to the
    // step that needed it. Among statements that name a path before it was
    // needed, the first one is where it came into being - nothing could have
    // read it earlier.
    let found = -1;
    let via = null;
    let how = null;
    let rank = -1;
    for (let i = 0; i < limit && !exhausted; i++) {
      if (usedIdx.has(i)) continue;
      // EVERY need is scored against this statement, not just the first one that
      // matches at all. A step that consumes two paths can `mention` one and have
      // WRITTEN the other, and stopping at the first match reports the weaker
      // evidence and credits the statement with the wrong path.
      for (const need of needs) {
        // Charged BEFORE the guard, so the budget counts every pair the walk
        // considers rather than only the ones that survive. Counting survivors
        // left the real bound at rounds x statements x needs while the comment
        // claimed a flat 20k, which is the kind of gap between code and comment
        // that outlives whoever wrote both.
        if (++checks > MAX_LINK_CHECKS) { exhausted = true; break; }
        if (i >= need.before) continue;
        const rule = linkFor(sts[i], writes[i], paths[i], need.path);
        if (!rule) continue;
        const r = LINK_RANK[rule];
        if (r > rank) { found = i; via = need; how = rule; rank = r; }
      }
    }
    if (found < 0) break;
    usedIdx.add(found);
    produced.add(via.path);
    chain.push({ idx: found, st: sts[found], produced: via.path, how });
    needs = needs.filter((n) => n.path !== via.path);
    for (const path of inputs[found]) {
      if (needs.length >= MAX_PENDING_NEEDS) break;
      if (produced.has(path) || needs.some((n) => n.path === path)) continue;
      needs.push({ path, before: found });
    }
  }
  // Execution order, not discovery order: the walk can reach a sibling's
  // producer after an earlier link, and the chain has to read the way the
  // command ran.
  chain.sort((a, b) => a.idx - b.idx);
  return chain.map(({ st, produced, how }) => ({
    line: clip(st.line, STATEMENT_CLIP),
    verb: st.verb,
    produced,
    how,
    // The paths the statement NAMES, minus the one it produced: the script being
    // run, the source of a copy, the input handed to a converter. This is what
    // the call came FROM, and it is a recorded path rather than an inference -
    // `python3 .../soffice.py` names soffice.py the same way `node build_deck.js`
    // names build_deck.js. Derived artifacts are frequently /tmp files the trace
    // never sees again, so a step's produced path is the weaker of the two.
    names: statementPaths(st).filter((p) => pathKey(p) !== pathKey(produced)),
  }));
}

function resultIndex(turns) {
  const byId = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) byId.set(b.tool_use_id, b);
    }
  }
  return byId;
}

function resultText(result) {
  const c = result?.content;
  if (typeof c === "string") return c;
  if (Array.isArray(c)) return c.map((p) => (typeof p === "string" ? p : p?.text || "")).join("");
  return "";
}

function resultFailed(result) {
  if (!result) return false;
  if (result.isError) return true;
  return resultText(result).includes("<tool_use_error>");
}

// djb2: the sibling libs' synthetic key, for a shell block with no id.
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/**
 * Files-domain ops for shell statements that named a write destination.
 *
 * `fileOps` is extractFileOps' output for the same turns: its paths are how a
 * path is SPELLED in this trace, and a claim has to land on that exact spelling
 * or it opens a second lane for one file. Ops carry the tool_use block id as
 * their key (suffixed per target) so they resolve to a real position and claim
 * the call through the same door as every other extractor.
 */
export function extractShellWriteOps(turns, fileOps = []) {
  const safe = Array.isArray(turns) ? turns : [];
  const known = new Map();
  for (const op of fileOps || []) {
    if (op?.path) known.set(pathKey(op.path), op.path);
  }
  const results = resultIndex(safe);
  const ops = [];
  const seen = new Set();
  for (const turn of safe) {
    for (const b of turn?.blocks || []) {
      const command = shellCommandOf(b);
      if (!command) continue;
      const blockId = b.id || `synth:shell:${hashString(command)}`;
      const result = b.id ? results.get(b.id) : null;
      const failed = resultFailed(result);
      const output = failed ? resultText(result) : "";
      let n = 0;
      for (const st of shellStatements(command)) {
        if (n >= WRITES_PER_COMMAND_CAP) break;
        for (const target of writeTargetsOf(st, known)) {
          if (n >= WRITES_PER_COMMAND_CAP) break;
          const key = pathKey(target);
          const dedup = `${blockId}|${key}`;
          if (seen.has(dedup)) continue;
          seen.add(dedup);
          ops.push({
            key: `${blockId}#sh${n++}`,
            kind: "create",
            path: known.get(key) || target,
            // A failed command keeps ALL its writes marked failed, including the
            // ones whose exit code was someone else's. See attributableFailure:
            // the mark stays conservative and the op line carries what we
            // actually know.
            ok: !failed,
            plus: 0,
            minus: 0,
            readLines: 0,
            shell: {
              statement: clip(st.line, STATEMENT_CLIP),
              verb: st.verb,
              ...(failed && !attributableFailure(st, output, target) ? { exitUnattributed: true } : {}),
            },
          });
        }
      }
    }
  }
  return ops;
}

/**
 * Shell statements that ran in a file's own directory at or before `beforeTurn`,
 * newest first. Where the WRITE tier reports what a command said it wrote, this
 * reports where to go looking when nothing said anything. A script that writes
 * its outputs by name from inside its own source leaves exactly this trace and
 * no more. Callers must present these as directory activity, never as this
 * file's provenance.
 *
 * `exclude` drops statements that already earned a WRITE claim on this path:
 * one that named the file has nothing to gain from also being offered as a
 * place to look, and listing it twice makes the weaker claim look like
 * corroboration of the stronger one.
 */
export function shellOriginHints(turns, path, { beforeTurn = Infinity, exclude = [] } = {}) {
  if (!path) return [];
  const safe = Array.isArray(turns) ? turns : [];
  const dir = dirOf(path);
  if (!dir) return [];
  const claimed = new Set(exclude);
  const hints = [];
  safe.forEach((turn, turnIndex) => {
    if (turnIndex > beforeTurn) return;
    for (const b of turn?.blocks || []) {
      const command = shellCommandOf(b);
      if (!command) continue;
      for (const st of shellStatements(command)) {
        if (INSPECT_VERBS.has(st.verb) || !st.verb) continue;
        const inDir = (st.cwd && pathKey(st.cwd) === dir) || st.line.includes(dir);
        if (!inDir) continue;
        const statement = clip(st.line, STATEMENT_CLIP);
        if (claimed.has(statement)) continue;
        hints.push({ turnIndex, statement, verb: st.verb });
      }
    }
  });
  return hints.slice(-HINTS_PER_FILE).reverse();
}
